import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";
import { Upload, Trash2, LogOut, ArrowLeft, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import SiteContentEditor from "@/components/SiteContentEditor";
import AccountSettings from "@/components/AccountSettings";

const CATEGORIES = [
    { value: "portraits", label: "Portraits" },
    { value: "gatherings", label: "Gatherings" },
    { value: "cars", label: "Cars" },
];

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const [photos, setPhotos] = useState([]);
    const [messages, setMessages] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("photos");

    // Upload form
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState("");
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("portraits");
    const [uploading, setUploading] = useState(false);

    const loadPhotos = async () => {
        try {
            const { data } = await api.get("/photos");
            setPhotos(data || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };
    const loadMessages = async () => {
        try {
            const { data } = await api.get("/contact");
            setMessages(data || []);
        } catch (e) {
            // ignore for non-admin
        }
    };

    useEffect(() => {
        setLoading(true);
        Promise.all([loadPhotos(), loadMessages()]).finally(() => setLoading(false));
    }, []);

    const onFileChange = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) {
            toast.error("Please choose an image file");
            return;
        }
        if (f.size > 8 * 1024 * 1024) {
            toast.error("Image must be under 8 MB");
            return;
        }
        setFile(f);
        const dataUrl = await fileToBase64(f);
        setPreview(dataUrl);
    };

    const onUpload = async (e) => {
        e.preventDefault();
        if (!preview) {
            toast.error("Choose a photo first");
            return;
        }
        setUploading(true);
        try {
            const { data } = await api.post("/photos", {
                title: title.trim(),
                category,
                image_base64: preview,
            });
            setPhotos([data, ...photos]);
            setFile(null);
            setPreview("");
            setTitle("");
            toast.success("Photo uploaded");
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setUploading(false);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this photo permanently?")) return;
        try {
            await api.delete(`/photos/${id}`);
            setPhotos(photos.filter((p) => p.id !== id));
            toast.success("Photo deleted");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const onDeleteMessage = async (id) => {
        if (!window.confirm("Delete this message?")) return;
        try {
            await api.delete(`/contact/${id}`);
            setMessages(messages.filter((m) => m.id !== id));
            toast.success("Message deleted");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const visiblePhotos = filter === "all" ? photos : photos.filter((p) => p.category === filter);

    return (
        <div data-testid="admin-dashboard-page" className="min-h-screen bg-[#F9F8F6]">
            {/* Header bar */}
            <header className="border-b border-[#E5DED5] bg-[#F9F8F6] sticky top-0 z-30 backdrop-blur-xl">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <Link to="/" data-testid="admin-back-to-site" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#5C5853] hover:text-[#1A1817]">
                            <ArrowLeft className="w-4 h-4" /> Site
                        </Link>
                        <span className="font-serif text-2xl tracking-tight">Admin</span>
                    </div>
                    <div className="flex items-center gap-6">
                        <span data-testid="admin-user-email" className="hidden sm:inline text-xs text-[#5C5853]">
                            {user?.email}
                        </span>
                        <button data-testid="admin-logout-btn" onClick={logout} className="btn-editorial !py-2 !px-4 !text-[0.7rem]">
                            <LogOut className="w-3.5 h-3.5" /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
                <p className="overline" data-testid="admin-eyebrow">Dashboard</p>
                <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-3">
                    Manage the studio.
                </h1>

                {/* Tabs */}
                <div className="mt-10 flex gap-8 border-b border-[#E5DED5] overflow-x-auto">
                    <button
                        data-testid="admin-tab-photos"
                        onClick={() => setTab("photos")}
                        className={`filter-btn !pb-4 ${tab === "photos" ? "active" : ""}`}
                    >
                        Photos ({photos.length})
                    </button>
                    <button
                        data-testid="admin-tab-content"
                        onClick={() => setTab("content")}
                        className={`filter-btn !pb-4 ${tab === "content" ? "active" : ""}`}
                    >
                        Home Page Content
                    </button>
                    <button
                        data-testid="admin-tab-messages"
                        onClick={() => setTab("messages")}
                        className={`filter-btn !pb-4 ${tab === "messages" ? "active" : ""}`}
                    >
                        Inquiries ({messages.length})
                    </button>
                    <button
                        data-testid="admin-tab-settings"
                        onClick={() => setTab("settings")}
                        className={`filter-btn !pb-4 ${tab === "settings" ? "active" : ""}`}
                    >
                        Settings
                    </button>
                </div>

                {tab === "settings" && (
                    <div className="mt-10" data-testid="admin-settings-section">
                        <AccountSettings />
                    </div>
                )}

                {tab === "content" && (
                    <div className="mt-10" data-testid="admin-content-section">
                        <SiteContentEditor />
                    </div>
                )}

                {tab === "photos" && (
                    <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                        {/* Upload */}
                        <section className="lg:col-span-4">
                            <p className="overline mb-4">New Upload</p>
                            <form onSubmit={onUpload} className="border border-[#E5DED5] bg-[#F2EFE9]/40 p-6 space-y-5" data-testid="admin-upload-form">
                                <label
                                    htmlFor="photo-input"
                                    data-testid="admin-upload-dropzone"
                                    className="block border border-dashed border-[#5C5853]/50 aspect-[4/3] cursor-pointer hover:bg-[#F9F8F6] transition-colors relative overflow-hidden"
                                >
                                    {preview ? (
                                        <img src={preview} alt="preview" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center text-[#5C5853]">
                                            <ImageIcon className="w-8 h-8 mb-3" />
                                            <p className="text-sm">Click to select an image</p>
                                            <p className="text-xs mt-1 opacity-70">JPG · PNG · up to 8 MB</p>
                                        </div>
                                    )}
                                </label>
                                <input
                                    id="photo-input"
                                    data-testid="admin-upload-file-input"
                                    type="file"
                                    accept="image/*"
                                    onChange={onFileChange}
                                    className="hidden"
                                />

                                <div>
                                    <label className="overline">Title</label>
                                    <input
                                        data-testid="admin-upload-title"
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="input-underline"
                                        placeholder="Untitled · No. 24"
                                    />
                                </div>

                                <div>
                                    <label className="overline">Category</label>
                                    <select
                                        data-testid="admin-upload-category"
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                        className="input-underline cursor-pointer"
                                    >
                                        {CATEGORIES.map((c) => (
                                            <option key={c.value} value={c.value}>{c.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <button
                                    type="submit"
                                    disabled={uploading || !preview}
                                    data-testid="admin-upload-submit"
                                    className="btn-editorial primary w-full justify-center disabled:opacity-50"
                                >
                                    {uploading ? "Uploading…" : (<>Upload Photo <Upload className="w-4 h-4" /></>)}
                                </button>
                            </form>
                        </section>

                        {/* Gallery */}
                        <section className="lg:col-span-8">
                            <div className="flex items-center justify-between mb-6">
                                <p className="overline">Library</p>
                                <div className="flex gap-5">
                                    {[{ key: "all", label: "All" }, ...CATEGORIES.map(c => ({ key: c.value, label: c.label }))].map((c) => (
                                        <button
                                            key={c.key}
                                            data-testid={`admin-filter-${c.key}`}
                                            onClick={() => setFilter(c.key)}
                                            className={`filter-btn ${filter === c.key ? "active" : ""}`}
                                        >
                                            {c.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {loading ? (
                                <p data-testid="admin-photos-loading" className="overline">Loading…</p>
                            ) : visiblePhotos.length === 0 ? (
                                <div data-testid="admin-photos-empty" className="border border-dashed border-[#E5DED5] p-16 text-center">
                                    <p className="overline">No photos yet</p>
                                    <p className="text-sm text-[#5C5853] mt-3">Upload your first frame using the panel on the left.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="admin-photos-grid">
                                    {visiblePhotos.map((p) => (
                                        <div key={p.id} data-testid={`admin-photo-${p.id}`} className="border border-[#E5DED5] bg-[#F9F8F6] group relative">
                                            <div className="aspect-[4/5] overflow-hidden">
                                                <img src={p.image_base64} alt={p.title} className="w-full h-full object-cover" />
                                            </div>
                                            <div className="p-3 flex items-center justify-between">
                                                <div className="min-w-0">
                                                    <p className="font-serif text-base truncate">{p.title || "Untitled"}</p>
                                                    <p className="overline mt-0.5">{p.category}</p>
                                                </div>
                                                <button
                                                    data-testid={`admin-photo-delete-${p.id}`}
                                                    onClick={() => onDelete(p.id)}
                                                    className="text-[#7A2E2E] hover:text-[#1A1817] p-2"
                                                    aria-label="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    </div>
                )}

                {tab === "messages" && (
                    <section className="mt-10" data-testid="admin-messages-section">
                        {loading ? (
                            <p className="overline">Loading…</p>
                        ) : messages.length === 0 ? (
                            <div className="border border-dashed border-[#E5DED5] p-16 text-center">
                                <p className="overline">No inquiries yet</p>
                            </div>
                        ) : (
                            <div className="border border-[#E5DED5]">
                                {messages.map((m, i) => (
                                    <div
                                        key={m.id}
                                        data-testid={`admin-message-${m.id}`}
                                        className={`p-6 ${i < messages.length - 1 ? "border-b border-[#E5DED5]" : ""}`}
                                    >
                                        <div className="flex items-start justify-between gap-4 flex-wrap">
                                            <div className="min-w-0">
                                                <p className="font-serif text-2xl">{m.name}</p>
                                                <p className="text-sm text-[#5C5853] mt-1">
                                                    <a href={`mailto:${m.email}`} className="link-underline">{m.email}</a>
                                                    {m.event_type ? ` · ${m.event_type}` : ""}
                                                </p>
                                                {m.subject && (
                                                    <p className="overline mt-3">RE: {m.subject}</p>
                                                )}
                                                <p className="text-sm text-[#1A1817] mt-3 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                                                <p className="text-xs text-[#5C5853] mt-4">{new Date(m.created_at).toLocaleString()}</p>
                                            </div>
                                            <button
                                                data-testid={`admin-message-delete-${m.id}`}
                                                onClick={() => onDeleteMessage(m.id)}
                                                className="text-[#7A2E2E] hover:text-[#1A1817] p-2 flex-shrink-0"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}
            </main>
        </div>
    );
}
